<template>
  <div class="job-card-equal-height card-max-w cursor-pointer">
    <component :is="rootTag" v-bind="rootAttrs">
      <div class="bg-white-primary content rounded-bottom-3 flex-grow-1 d-flex flex-column h-100">
        <div v-if="showImageWrapper" class="job-card__image-wrapper">
          <div v-if="isImageLoading" class="job-card__image-placeholder w-100 h-100">
            <span class="visually-hidden">Loading image...</span>
          </div>
          <img
            v-else-if="resolvedImageUrl && !isImageError"
            :src="resolvedImageUrl"
            :alt="imageAlt || 'Job image'"
            class="job-card__image w-100 h-100"
            @load="onImageLoad"
            @error="onImageError"
          />
          <img
            v-else
            :src="fallbackImageUrl"
            alt="デフォルト画像"
            class="job-card__image w-100 h-100"
          />
          <span v-if="showNewChip" class="job-card__chip job-card__chip--new">新着</span>
        </div>

        <div
          :class="['d-flex flex-column h-100 job-card__content', { 'has-button': hasBottomButton }]"
        >
          <div class="job-card__header">
            <p class="mb-2 size-12 lh-12 job-card__company job-card__company--emphasis">
              {{ companyName }}
            </p>
            <div class="color-black-02">
              <component
                :is="titleTag"
                :href="hrefOrUndefined"
                class="text-reset text-decoration-none d-block"
              >
                <h3 class="size-16 fw-bold job-card__title job-card__title--emphasis">
                  {{ jobTitle }}
                </h3>
              </component>
            </div>
          </div>

          <div class="job-card__body">
            <template v-if="hasOccupation">
              <div class="job-occupation d-flex align-items-center job-card__row-pad">
                <img :src="icons.occupation" alt="Occupation icon" />
                <span class="size-10 ps-1 job-card__text-ellipsis job-card__meta-text">
                  {{ occupationDescriptions!.filter(Boolean).join(', ') }}
                </span>
              </div>
            </template>

            <!--
            <template v-if="hasIndustry">
              <div class="job-industry d-flex align-items-center job-card__row-pad">
                <img :src="icons.industry" alt="Industry icon" />
                <span class="size-10 ps-1 job-card__text-ellipsis job-card__meta-text">
                  {{ industryNames!.filter(Boolean).join(', ') }}
                </span>
              </div>
            </template>
            -->

            <div class="d-flex gap-2">
              <template v-if="hasIncome">
                <div class="job-income job-card__income d-flex align-items-center">
                  <img :src="icons.income" alt="Income icon" />
                  <span class="size-10 ps-1 job-card__meta-text">
                    <template v-if="wageMinNumber !== undefined && wageMaxNumber !== undefined">
                      <span class="job-card__income-prefix">{{ wageMinNumber }}</span>
                      <span class="job-card__income-separator">~</span>
                      <span class="job-card__income-amount">{{ wageMaxNumber }}</span>
                      <span class="job-card__income-unit">万円</span>
                    </template>
                    <template v-else-if="wageMinNumber !== undefined">
                      <span class="job-card__income-prefix">{{ wageMinNumber }}</span>
                      <span class="job-card__income-separator">~</span>
                    </template>
                    <template v-else-if="wageMaxNumber !== undefined">
                      <span class="job-card__income-separator">~</span>
                      <span class="job-card__income-amount">{{ wageMaxNumber }}</span>
                      <span class="job-card__income-unit">万円</span>
                    </template>
                  </span>
                </div>
              </template>

              <template v-if="hasPrefecture">
                <div class="job-place job-card__place d-flex align-items-center">
                  <img :src="icons.place" alt="Place icon" />
                  <span class="size-10 ps-1 job-card__text-ellipsis job-card__meta-text">
                    {{ filteredPrefectures.join(', ') }}
                  </span>
                </div>
              </template>
            </div>

            <template v-if="!hideSkills && hasSkills">
              <div class="job-card__skills job-card__row-pad">
                <img :src="icons.skill" alt="Skill icon" class="job-card__skill-icon" />
                <div class="job-card__skill-tags-wrapper">
                  <div ref="skillTagsRef" class="job-card__skill-tags">
                    <template v-for="(skill, index) in normalizedSkills" :key="`skill-${index}`">
                      <div class="job-card__skill-tag job-card__skill-tag--accent">
                        <span class="size-10 job-card__skill-text job-card__skill-text--accent">
                          {{ skill }}
                        </span>
                      </div>
                    </template>
                  </div>
                  <span v-if="isSkillOverflow" class="size-10 job-card__skill-ellipsis">...</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </component>

    <!-- Bottom fixed button area (absolute) - outside anchor -->
    <div v-if="hasBottomButton" class="job-card__bottom-button d-flex justify-content-center">
      <slot name="bottomButton">
        <LoadingButton
          v-if="showApplyButton"
          :mode="'apply'"
          :variant="'primary'"
          :button-style="'filled'"
          :action="applyAction || defaultApplyAction"
          :custom-size="true"
          :rounded="false"
          class="rounded-24 px-5 py-2 size-12"
        >
          応募する
        </LoadingButton>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, onUpdated, ref, useSlots } from 'vue';
import LoadingButton from '../button/LoadingButton.vue';

import type { JobCardProps } from './types';

const props = withDefaults(defineProps<JobCardProps>(), {
  href: undefined,
  imageUrl: undefined,
  imageAlt: undefined,
  occupationDescriptions: () => [],
  industryNames: () => [],
  prefectureNames: () => [],
  skills: () => [],
  hideSkills: false,
  showApplyButton: false,
  showNewChip: false,
  showImageWrapper: true,
});

const defaultApplyAction = async () => {
  await new Promise((resolve) => setTimeout(resolve, 800));
};

const icons = {
  occupation: new URL('../../../assets/images/icons/ic_occupation.svg', import.meta.url).href,
  industry: new URL('../../../assets/images/icons/ic_industry.svg', import.meta.url).href,
  income: new URL('../../../assets/images/icons/ic_income.svg', import.meta.url).href,
  place: new URL('../../../assets/images/icons/ic_round-place-blue.svg', import.meta.url).href,
  skill: new URL('../../../assets/images/icons/gear-white.svg', import.meta.url).href,
};

const hasOccupation = computed(
  () => props.occupationDescriptions && props.occupationDescriptions.filter(Boolean).length > 0
);
const hasIndustry = computed(
  () => props.industryNames && props.industryNames.filter(Boolean).length > 0
);
const hasSkills = computed(() => props.skills && props.skills.filter(Boolean).length > 0);
const filteredPrefectures = computed(() =>
  (props.prefectureNames || []).filter((name) => name && name !== '未選択')
);
const hasPrefecture = computed(() => filteredPrefectures.value.length > 0);

const wageMinNumber = computed(() =>
  props.wageMin === undefined || props.wageMin === null || props.wageMin === ''
    ? undefined
    : Number(props.wageMin)
);
const wageMaxNumber = computed(() =>
  props.wageMax === undefined || props.wageMax === null || props.wageMax === ''
    ? undefined
    : Number(props.wageMax)
);
const hasIncome = computed(
  () => wageMinNumber.value !== undefined || wageMaxNumber.value !== undefined
);

const normalizedSkills = computed(() =>
  (props.skills || [])
    .filter(Boolean)
    .flatMap((skill) => skill.split(',').map((s) => s.trim()))
    .filter(Boolean)
);
// Skill overflow detection (no wrap, hide overflow, show ellipsis by JS)
const skillTagsRef = ref<HTMLElement | null>(null);
const isSkillOverflow = ref(false);

const recalcSkillOverflow = async () => {
  await nextTick();
  const el = skillTagsRef.value;
  if (!el) return;
  // Compare scrollWidth and clientWidth to determine horizontal overflow
  isSkillOverflow.value = el.scrollWidth > el.clientWidth;
};

const hasImage = computed(() => props.imageUrl !== undefined && props.imageUrl !== '');
const slots = useSlots();
const hasBottomButton = computed(() => !!slots.bottomButton || !!props.showApplyButton);
const showImageWrapper = computed(() => props.showImageWrapper);

const rootTag = computed(() => (props.href && !props.showApplyButton ? 'a' : 'div'));
const rootAttrs = computed(() => {
  if (props.href && !props.showApplyButton) {
    return {
      href: props.href,
      class:
        'text-decoration-none color-black-primary content h-100 rounded-3 overflow-hidden border-md-0 border border-grey-10 job-card__container d-flex flex-column',
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }
  return {
    class:
      'rounded-3 overflow-hidden border-md-0 border border-grey-10 h-100 job-card__container d-flex flex-column',
  };
});

const titleTag = computed(() => (props.href && props.showApplyButton ? 'a' : 'span'));
const hrefOrUndefined = computed(() =>
  props.href && props.showApplyButton ? props.href : undefined
);

// 画像取得状態管理
const isImageLoading = ref(false);
const isImageError = ref(false);
const resolvedImageUrl = ref<string | undefined>(undefined);

const fallbackImageUrl = new URL('../../../assets/images/image-empty.png', import.meta.url).href;
const showImagePlaceholder = computed(() => isImageLoading.value && !isImageError.value);

const onImageLoad = () => {
  isImageLoading.value = false;
};

const onImageError = () => {
  isImageLoading.value = false;
  isImageError.value = true;
};

onMounted(() => {
  if (props.imageUrl) {
    // 画像をプリロードして確実に onload/onerror が発火するようにする
    isImageLoading.value = true;
    resolvedImageUrl.value = props.imageUrl;
    const img = new Image();
    img.onload = () => {
      isImageLoading.value = false;
    };
    img.onerror = () => {
      isImageError.value = true;
      isImageLoading.value = false;
    };
    img.src = props.imageUrl;
  } else {
    // 画像がない場合は即座にデフォルト画像を表示
    resolvedImageUrl.value = undefined;
    isImageError.value = true;
    isImageLoading.value = false;
  }
});

onMounted(() => {
  recalcSkillOverflow();
  window.addEventListener('resize', recalcSkillOverflow);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', recalcSkillOverflow);
});

nextTick(() => recalcSkillOverflow());
onUpdated(() => recalcSkillOverflow());
</script>

<style scoped lang="scss">
.job-card-equal-height {
  position: relative;
}

.card-max-w {
  max-width: 25rem;
  width: 100%;
}

.job-card__company {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-card__company--emphasis {
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 133%;
  letter-spacing: 0%;
  color: #1b1b1b;
}

.job-card__title {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: clip;
  white-space: normal;
  word-break: break-all;
  overflow-wrap: anywhere;
  width: 100%;
  line-height: 1.4;
}

.job-card__title--emphasis {
  font-weight: 700;
  font-size: 1rem;
  line-height: 140%;
  letter-spacing: 0;
  color: #353840;
  min-height: calc(1em * 4.2);
}

.job-card__image {
  height: 11.25rem;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
  will-change: transform;
}

.job-card__image-wrapper {
  position: relative;
  height: 11.25rem;
  overflow: hidden;
}

.job-card__image-placeholder {
  height: 11.25rem;
  background: linear-gradient(90deg, #f2f4f7 0%, #e9eef3 50%, #f2f4f7 100%);
  background-size: 200% 100%;
  animation: job-card-image-loading 1.2s ease-in-out infinite;
}

@keyframes job-card-image-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.job-card-equal-height:hover .job-card__image {
  transform: scale(1.03);
}

.job-card__content {
  padding-top: 0.5rem;
  padding-right: 1rem;
  padding-bottom: 1rem;
  padding-left: 1rem;
}

.job-card-equal-height .job-card__content.has-button {
  padding-bottom: 4.25rem;
  padding-top: 0.5rem;
}

.job-card__bottom-button {
  position: absolute;
  left: 50%;
  bottom: 1rem;
  transform: translate(-50%);
  width: 100%;
  padding: 0 1rem;
}

.job-card__chip {
  position: absolute;
  top: 0;
  left: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.1875rem;
  height: 1.5rem;
  padding: 0.25rem 0.75rem;
  border-top-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  color: $white-01;
  font-size: 0.75rem;
  line-height: 1;
}

.job-card__chip--new {
  background: #e60012;
}

.job-card__text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.job-card__meta-text {
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 133%;
  letter-spacing: 0%;
  color: #1b1b1b;
}

.job-card__row-pad {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.job-card__skill-tags-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.job-card__skill-tags {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;
  white-space: nowrap;
  max-width: calc(100% - 2.5rem);
}

.job-card__skill-tag {
  display: inline-flex;
  align-items: center;
  height: 1.25rem;
  padding: 0 0.5rem;
  border: 0.0625rem solid #aab4c3;
  border-radius: 0.75rem;
}

.job-card__skill-tag--accent {
  border-radius: 1.75rem;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: #e1f6f7;
  border: none;
}

.job-card__skill-text {
  display: inline-block;
  max-width: 100%;
}

.job-card__skill-text--accent {
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 133%;
  letter-spacing: 0%;
  color: #0a8da1;
}

.job-card__skill-ellipsis {
  display: inline-block;
  flex: 0 0 1rem;
  text-align: left;
  padding-left: 0.5rem;
}
</style>
